import * as React from 'react';
import { Row, Button } from '@simplywallst/ui-core';
import { SplitColumn, StatementWrapper } from './styled';
import Statement from '@/components/Statement';
import Description from '@components/Statement/components/Description';
import ShareHolderReturnsSummary from './components/ShareHolderReturnsSummary';
import { shallowEqual, useSelector } from 'react-redux';
import { COMPANY_RETURNS } from '@/constants/feedbackSections';
import ShareHolderReturns from './components/ShareHolderReturns';
import PriceVolatilityMarket from './components/PriceVolatilityMarket';
import Modal from '@/components/Modal';
import selector from './selector';

const getProp = <T,>(target?: T, prop?: keyof T) => {
  if (
    typeof target !== 'undefined' &&
    target !== null &&
    typeof prop !== 'undefined' &&
    typeof target[prop] !== 'undefined' &&
    target[prop] !== null
  ) {
    return target[prop];
  }
  return 'n/a';
};

export function MarketPerformance() {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const [showReturnsModal, setShowReturnsModal] = React.useState(false);
  return (
    <Row>
      <SplitColumn col={12} colMd={6}>
        <ShareHolderReturnsSummary
          exchangeCountryISO={selected.exchangeCountryISO}
          industryName={selected.industryName}
          uniqueSymbol={selected.tickerSymbol}
          company7D={selected.companyPerformance.return7D}
          company7DFormatted={selected.companyPerformance.return7DFormatted}
          company1YrAbs={selected.companyPerformance.return1YrAbs}
          company1YrAbsFormatted={
            selected.companyPerformance.return1YrAbsFormatted
          }
          industry7D={selected.industryPerformance?.return7D || null}
          industry7DFormatted={
            selected.industryPerformance?.return7DFormatted || 'n/a'
          }
          industry1YrAbs={selected.industryPerformance?.return1YrAbs || null}
          industry1YrAbsFormatted={
            selected.industryPerformance?.return1YrAbsFormatted || 'n/a'
          }
          market7D={selected.marketPerformance?.return7D || null}
          market7DFormatted={
            selected.marketPerformance?.return7DFormatted || 'n/a'
          }
          market1YrAbs={selected.marketPerformance?.return1YrAbs || null}
          market1YrAbsFormatted={
            selected.marketPerformance?.return1YrAbsFormatted || 'n/a'
          }
        />
        <Button
          display="block"
          styleType="loudLarge"
          onClick={() => setShowReturnsModal(true)}
        >
          See full shareholder returns
        </Button>
        <StatementWrapper>
          {selected.statements.is1YearReturnInLineOrAboveIndustry && (
            <Statement
              noIcon
              title={
                selected.statements.is1YearReturnInLineOrAboveIndustry.title
              }
              description={
                <Description
                  statement={
                    selected.statements.is1YearReturnInLineOrAboveIndustry
                  }
                />
              }
              value={
                selected.statements.is1YearReturnInLineOrAboveIndustry.value
              }
            />
          )}
          {selected.statements.is1YearReturnInLineOrAboveMarket && (
            <Statement
              noIcon
              title={selected.statements.is1YearReturnInLineOrAboveMarket.title}
              description={
                <Description
                  statement={
                    selected.statements.is1YearReturnInLineOrAboveMarket
                  }
                />
              }
              value={selected.statements.is1YearReturnInLineOrAboveMarket.value}
            />
          )}
        </StatementWrapper>
        {showReturnsModal && (
          <Modal
            onClose={() => setShowReturnsModal(false)}
            onPrimaryClick={() => setShowReturnsModal(false)}
            primaryLabel="Close"
            hideSecondary
            title="Shareholder Returns"
          >
            <ShareHolderReturns
              uniqueSymbol={selected.tickerSymbol}
              company7D={getProp(
                selected.companyPerformance,
                'return7DFormatted'
              )}
              company30D={getProp(
                selected.companyPerformance,
                'return30DFormatted'
              )}
              company90D={getProp(
                selected.companyPerformance,
                'return90DFormatted'
              )}
              company1YrAbs={getProp(
                selected.companyPerformance,
                'return1YrAbsFormatted'
              )}
              company1YrTotal={getProp(
                selected.companyPerformance,
                'return1YrTotalReturnFormatted'
              )}
              company3YrAbs={getProp(
                selected.companyPerformance,
                'return3YrAbsFormatted'
              )}
              company3YrTotal={getProp(
                selected.companyPerformance,
                'return3YrTotalReturnFormatted'
              )}
              company5YrAbs={getProp(
                selected.companyPerformance,
                'return5YrAbsFormatted'
              )}
              company5YrTotal={getProp(
                selected.companyPerformance,
                'return5YrTotalReturnFormatted'
              )}
              industry7D={getProp(
                selected.industryPerformance,
                'return7DFormatted'
              )}
              industry30D={getProp(
                selected.industryPerformance,
                'return30DFormatted'
              )}
              industry90D={getProp(
                selected.industryPerformance,
                'return90DFormatted'
              )}
              industry1YrAbs={getProp(
                selected.industryPerformance,
                'return1YrAbsFormatted'
              )}
              industry1YrTotal={getProp(
                selected.industryPerformance,
                'return1YrTotalReturnFormatted'
              )}
              industry3YrAbs={getProp(
                selected.industryPerformance,
                'return3YrAbsFormatted'
              )}
              industry3YrTotal={getProp(
                selected.industryPerformance,
                'return3YrTotalReturnFormatted'
              )}
              industry5YrAbs={getProp(
                selected.industryPerformance,
                'return5YrAbsFormatted'
              )}
              industry5YrTotal={getProp(
                selected.industryPerformance,
                'return5YrTotalReturnFormatted'
              )}
              market7D={getProp(
                selected.marketPerformance,
                'return7DFormatted'
              )}
              market30D={getProp(
                selected.marketPerformance,
                'return30DFormatted'
              )}
              market90D={getProp(
                selected.marketPerformance,
                'return90DFormatted'
              )}
              market1YrAbs={getProp(
                selected.marketPerformance,
                'return1YrAbsFormatted'
              )}
              market3YrAbs={getProp(
                selected.marketPerformance,
                'return3YrAbsFormatted'
              )}
              market5YrAbs={getProp(
                selected.marketPerformance,
                'return5YrAbsFormatted'
              )}
              market1YrTotal={getProp(
                selected.marketPerformance,
                'return1YrTotalReturnFormatted'
              )}
              market3YrTotal={getProp(
                selected.marketPerformance,
                'return3YrTotalReturnFormatted'
              )}
              market5YrTotal={getProp(
                selected.marketPerformance,
                'return5YrTotalReturnFormatted'
              )}
            />
          </Modal>
        )}
      </SplitColumn>
      <SplitColumn col={12} colMd={6}>
        <PriceVolatilityMarket
          country={selected.country}
          industry={selected.industryName}
          tickerSymbol={selected.tickerSymbol}
          companyName={selected.uniqueSymbol}
          feedbackLabel={COMPANY_RETURNS}
        />
        <StatementWrapper>
          {selected.statements.hasPriceStability ? (
            <Statement
              noIcon
              title={selected.statements.hasPriceStability.title}
              description={
                <Description
                  statement={selected.statements.hasPriceStability}
                />
              }
              value={selected.statements.hasPriceStability.value}
            />
          ) : null}
          <Statement
            noIcon
            title={selected.statements.hasVolatilityImproved.title}
            description={
              <Description
                statement={selected.statements.hasVolatilityImproved}
              />
            }
            value={selected.statements.hasVolatilityImproved.value}
          />
        </StatementWrapper>
      </SplitColumn>
    </Row>
  );
}
